import { useQueryClient } from "@tanstack/vue-query";

import { useFollowCategoryMutation } from "~/queries/catalog/categoryPreferences";
import { useFollowEventMutation } from "~/queries/catalog/eventPreferences";
import {
  followedCategoriesListQueryKeys,
  meCategoryPreferenceDefaultParams,
} from "~/queries/catalog/mePreferenceCategories";
import { useFollowProductMutation } from "~/queries/catalog/productPreferences";

/**
 * Automatically completes a follow action that was triggered before the user logged-in, once they are logged-in.
 *
 * Should only be registered to the Vue lifecycle once.
 */
export default () => {
  if (import.meta.server) return;

  const { onboardingEntry } = useOnboardingDialog();

  const { mutateAsync: followProduct } = useFollowProductMutation();
  const { mutateAsync: followCategory } = useFollowCategoryMutation();
  const { mutateAsync: followEvent } = useFollowEventMutation();

  const platformId = computed(
    () => platformStore?.getPlatformConfiguration.platformId,
  );
  const queryClient = useQueryClient();
  const platformStore = usePlatformStore();
  const toast = useToast();

  const { t } = useI18n();

  const checkAutomaticFollow = () => {
    if (
      !onboardingEntry.value ||
      !onboardingEntry.value.data ||
      !("followType" in onboardingEntry.value.data)
    ) {
      return;
    }

    const { followType } = onboardingEntry.value.data;

    const id = onboardingEntry.value.data.id;
    const name = onboardingEntry.value.data.name;
    const toastMessage = t("customerPreferences.followFeedback.generic", {
      item: name,
    });

    const followCategoryOnce = async () => {
      const followedCategories = await queryClient.fetchQuery(
        followedCategoriesListQueryKeys.list({
          xPlatformKey: platformId.value,
          ...meCategoryPreferenceDefaultParams,
        }),
      );
      if (
        followedCategories &&
        !followedCategories.categoryIds.some((c) => c === id)
      ) {
        followCategory({
          id,
          newFollowStatus: true,
          trackData: {
            followType:
              "followSource" in (onboardingEntry.value?.data ?? {})
                ? (onboardingEntry.value?.data as any).followSource
                : undefined,
          },
        }).then(() => {
          toast.success(toastMessage);
        });
      }
    };

    switch (followType) {
      case FollowFeatureType.Category:
        followCategoryOnce();
        break;
      case FollowFeatureType.Product:
        followProduct({
          id,
          newFollowStatus: true,
          trackData: {
            followType: onboardingEntry.value.data.followSource,
          },
        }).then(() => {
          toast.success(toastMessage);
        });
        break;
      case FollowFeatureType.Event:
        followEvent({
          id,
          newFollowStatus: true,
          trackData: {
            followType: onboardingEntry.value.data.followSource,
          },
        }).then(() => {
          toast.success(toastMessage);
        });
        break;
    }
  };

  const { isLoggedIn } = useAuth();
  watch(
    [isLoggedIn, onboardingEntry],
    ([newIsLoggedIn, newOnboardingEntry]) => {
      if (newIsLoggedIn && newOnboardingEntry) checkAutomaticFollow();
    },
    { immediate: true, deep: true },
  );
};
